import React, { useCallback, useEffect, useMemo, useState } from 'react';

import qs from 'qs';
import { Helmet } from 'react-helmet';
import { Link } from '~/lib/react-router-dom';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Container,
  GridLegacy as Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Link as MuiLink,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';

import {
  type ChannelMarkupConditionApplyType,
  channelMarkupConditionApplyTypeDisplayNames,
} from '@luxuryescapes/contract-svc-promo/channelMarkup';

import CopyableField from '~/components/Common/CopyableField';
import GridPagination from '~/components/Common/Elements/GridPagination';
import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { toPromoBrand } from '~/consts/promoBrand';
import { ROLE_CHANNEL_MARKUP_ADMIN_USER } from '~/consts/roles';

import { useGetChannelMarkupV2List } from '~/hooks/apis/promo/useGetChannelMarkupV2List';
import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentTenant from '~/hooks/useCurrentTenant';
import useCurrentUser from '~/hooks/useCurrentUser';
import useQuery from '~/hooks/useQuery';

import type { ChannelMarkupV2Internal } from '~/services/PromoService';
import { customerPortalHost } from '~/services/common';

import dateFormatter from '~/utils/dateFormatter';

import useConfirmDeleteChannelMarkupV2 from './useConfirmDeleteChannelMarkupV2';

const sharedOpts: Partial<GridColDef> = {
  editable: false,
  sortable: false,
  filterable: false,
  hideable: false,
  disableColumnMenu: true,
  flex: 1,
};

const CHANNEL_MARKUP_V2_SORT_FIELDS = ['created_at', 'expires_at'] as const;
type ChannelMarkupV2SortField = (typeof CHANNEL_MARKUP_V2_SORT_FIELDS)[number];

const CHANNEL_MARKUP_STATUSES = ['active', 'expired', 'all'] as const;
type ChannelMarkupStatus = (typeof CHANNEL_MARKUP_STATUSES)[number];
// Most visits are to manage live markups, so show active ones unless the URL asks otherwise.
const DEFAULT_CHANNEL_MARKUP_STATUS: ChannelMarkupStatus = 'active';

function getChannelMarkupStatus(status: string | null): ChannelMarkupStatus {
  return status === 'active' || status === 'expired' || status === 'all' ? status : DEFAULT_CHANNEL_MARKUP_STATUS;
}

function isChannelMarkupV2SortField(field: string | undefined): field is ChannelMarkupV2SortField {
  return field === 'created_at' || field === 'expires_at';
}

export function getChannelMarkupV2SortModel(sortBy: string | null, sortByDirection: string | null): GridSortModel {
  if (!isChannelMarkupV2SortField(sortBy ?? undefined)) {
    return [];
  }

  return [
    {
      field: sortBy,
      sort: sortByDirection === 'desc' ? 'desc' : 'asc',
    },
  ];
}

export function formatRuleCondition(condition: {
  conditionType?: string | null;
  conditionApplyType?: string | null;
  conditionValue?: string | null;
  shortKey?: string | null;
}): string {
  const shortKey = condition.shortKey?.trim();
  if (shortKey) {
    return shortKey;
  }

  const conditionType = condition.conditionType?.trim() || 'n/a';
  const conditionValue = condition.conditionValue?.trim() || 'n/a';
  const conditionApplyType = condition.conditionApplyType?.trim();
  const applyTypeLabel = isChannelMarkupConditionApplyType(conditionApplyType)
    ? channelMarkupConditionApplyTypeDisplayNames[conditionApplyType]
    : conditionApplyType;

  return `${applyTypeLabel ? `${applyTypeLabel} ` : ''}${conditionType}: ${conditionValue}`;
}

const isChannelMarkupConditionApplyType = (value: string | undefined): value is ChannelMarkupConditionApplyType =>
  value === 'MUST-INCLUDE' || value === 'MUST-INCLUDE-ALL' || value === 'MUST-EXCLUDE';

function formatRuleMarkup(markup: {
  shortKey?: string | null;
  markupType?: string | null;
  currency?: string | null;
  markupValue?: number | null;
  luxPlusMarkupValue?: number | null;
}): string {
  const markupLabel = markup.shortKey?.trim() || markup.markupType?.trim() || 'n/a';
  const currencyLabel = markup.currency?.trim() || 'ANY';
  const markupValueLabel =
    markup.markupValue === null || markup.markupValue === undefined ? '--' : String(markup.markupValue);
  const luxPlusLabel =
    markup.luxPlusMarkupValue === null || markup.luxPlusMarkupValue === undefined
      ? ''
      : ` / Lux Plus ${markup.luxPlusMarkupValue}`;

  return `${markupLabel} · ${currencyLabel} · ${markupValueLabel}${luxPlusLabel}`;
}

export function ChannelMarkupV2ListIdCell({
  channelMarkupId,
  shortKey,
}: {
  channelMarkupId: string;
  shortKey?: string | null;
}) {
  const displayKey = shortKey?.trim() || channelMarkupId;

  return (
    <Stack spacing={0.25} sx={{ py: 0.5 }}>
      <MuiLink component={Link} to={`/marketing/channel-markup-v2/${channelMarkupId}/edit`}>
        {displayKey}
      </MuiLink>
      <CopyableField
        value={channelMarkupId}
        variant="caption"
        color="text.secondary"
        tooltip="Copy full channel markup ID"
        sx={{ overflowWrap: 'anywhere' }}
      />
    </Stack>
  );
}

const getColumns = ({
  tenant,
  canEditMarkup,
  deletingChannelMarkupId,
  handleEditChannelMarkup,
  handleDeleteChannelMarkup,
}: {
  tenant: App.Tenant;
  canEditMarkup: boolean;
  deletingChannelMarkupId: string | undefined;
  handleEditChannelMarkup: (row: ChannelMarkupV2Internal) => void;
  handleDeleteChannelMarkup: (row: ChannelMarkupV2Internal) => void;
}): Array<GridColDef<ChannelMarkupV2Internal>> => [
  {
    field: 'channel_markup_id',
    headerName: 'ID',
    renderCell: (params) => {
      const channelMarkupId = params.row.channelMarkupId;

      const shortKey = params.row.rules?.markups?.[0]?.shortKey;

      return channelMarkupId ? (
        <ChannelMarkupV2ListIdCell channelMarkupId={channelMarkupId} shortKey={shortKey} />
      ) : null;
    },
    ...sharedOpts,
  },
  {
    field: 'rules',
    headerName: 'Rules',
    renderCell: (params) => {
      const conditions = params.row.rules?.conditions ?? [];
      const markups = params.row.rules?.markups ?? [];
      return (
        <Stack spacing={0.5} sx={{ py: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Version:{' '}
            <Box component="span" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
              {params.row.version}
            </Box>
          </Typography>
          {markups.length > 0 && (
            <Stack spacing={0.25}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                Markups
              </Typography>
              {markups.map((markup, index) => (
                <Typography
                  key={markup.markupId ?? `${params.row.channelMarkupId ?? 'markup'}-${index}`}
                  variant="body2"
                >
                  {formatRuleMarkup(markup)}
                </Typography>
              ))}
            </Stack>
          )}
          {conditions.length > 0 && (
            <Stack spacing={0.25}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                Conditions
              </Typography>
              {conditions.map((condition, index) => (
                <Typography
                  key={condition.conditionId ?? `${params.row.channelMarkupId ?? 'condition'}-${index}`}
                  variant="body2"
                >
                  {formatRuleCondition(condition)}
                </Typography>
              ))}
            </Stack>
          )}
          {conditions.length === 0 && markups.length === 0 && <Typography variant="body2">-</Typography>}
        </Stack>
      );
    },
    minWidth: 420,
    ...sharedOpts,
  },
  {
    field: 'link',
    headerName: 'Link',
    ...sharedOpts,
    renderCell: (params) => {
      const firstMarkup = params.row.rules?.markups?.[0];
      const query = firstMarkup?.currency ? qs.stringify({ currency: firstMarkup.currency }) : '';
      return <MuiLink href={`${customerPortalHost(tenant)}${query ? `?${query}` : ''}`}>Customer Portal Link</MuiLink>;
    },
  },
  {
    field: 'created_at',
    headerName: 'Created At',
    renderCell: (params) => dateFormatter(params.row.createdAt ?? params.row.created_at),
    ...sharedOpts,
    sortable: true,
  },
  {
    field: 'expires_at',
    headerName: 'Expires At',
    renderCell: (params) => dateFormatter(params.row.expiresAt ?? params.row.expires_at),
    ...sharedOpts,
    sortable: true,
  },
  {
    field: 'warnings',
    headerName: 'Warnings',
    renderCell: (params) => {
      const warnings = params.row.warnings ?? [];
      if (warnings.length === 0) {
        return <Typography>-</Typography>;
      }

      return (
        <Stack spacing={0.25}>
          {warnings.map((warning) => (
            <Typography key={warning.message}>{warning.message}</Typography>
          ))}
        </Stack>
      );
    },
    ...sharedOpts,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    renderCell: (params) =>
      params.row.channelMarkupId ? (
        <Stack direction="row" spacing={0.5}>
          <IconButton
            aria-label={`Edit channel markup ${params.row.channelMarkupId}`}
            onClick={() => handleEditChannelMarkup(params.row)}
            size="small"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          {canEditMarkup && (
            <IconButton
              aria-label={`Delete channel markup ${params.row.channelMarkupId}`}
              onClick={() => handleDeleteChannelMarkup(params.row)}
              disabled={deletingChannelMarkupId === params.row.channelMarkupId}
              color="error"
              size="small"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      ) : (
        <Tooltip title="Editing is unavailable because this response has no channel markup ID.">
          <Box component="span">
            <IconButton aria-label="Edit channel markup unavailable" disabled size="small">
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        </Tooltip>
      ),
    ...sharedOpts,
  },
];

export default function ChannelMarkupV2ListPage() {
  const query = useQuery();
  const { push } = useAppNavigation();
  const { user } = useCurrentUser();
  const { confirmAndDelete, deleteChannelMarkupMutation } = useConfirmDeleteChannelMarkupV2();
  const [pageNum, setPageNum] = useState<number>(query.get('page_num') ? Number(query.get('page_num')) : 0);
  const [pageSize, setPageSize] = useState<number>(query.get('page_size') ? Number(query.get('page_size')) : 10);
  const { tenant } = useCurrentTenant();
  const [status, setStatus] = useState<ChannelMarkupStatus>(() => getChannelMarkupStatus(query.get('status')));
  const [sortModel, setSortModel] = useState<GridSortModel>(() =>
    getChannelMarkupV2SortModel(query.get('sort_by'), query.get('sort_by_direction')),
  );
  const canEditMarkup = Boolean(user?.roles?.includes(ROLE_CHANNEL_MARKUP_ADMIN_USER));
  const activeSort = sortModel[0];
  const activeSortBy = isChannelMarkupV2SortField(activeSort?.field) ? activeSort.field : undefined;
  const activeSortDirection = activeSort?.sort === 'asc' || activeSort?.sort === 'desc' ? activeSort.sort : undefined;
  const listQuery = useMemo(
    () => ({
      page: pageNum,
      pageSize,
      brand: toPromoBrand(tenant.brand),
      status,
      sortBy: activeSortBy,
      sortByDirection: activeSortDirection,
    }),
    [activeSortBy, activeSortDirection, pageNum, pageSize, status, tenant.brand],
  );
  const { data, isError, isFetching } = useGetChannelMarkupV2List({ query: listQuery });
  const channelMarkupList = data?.channelMarkupList ?? [];
  const totalCount = Number(data?.total ?? 0);

  const setQueryParams = useCallback(() => {
    const searchParams = qs.stringify({
      page_num: pageNum.toString(),
      page_size: pageSize.toString(),
      status,
      sort_by: activeSortBy,
      sort_by_direction: activeSortDirection,
    });
    push({
      search: `?${searchParams}`,
    });
  }, [activeSortBy, activeSortDirection, push, pageNum, pageSize, status]);

  useEffect(() => {
    setQueryParams();
  }, [setQueryParams]);

  const onPageChange = useCallback((page: number, newPageSize: number) => {
    setPageNum(page);
    setPageSize(newPageSize);
  }, []);

  const handleStatusChange = useCallback((nextStatus: ChannelMarkupStatus) => {
    setPageNum(0);
    setStatus(nextStatus);
  }, []);

  const handleSortModelChange = useCallback((nextSortModel: GridSortModel) => {
    const nextSort = nextSortModel[0];

    setPageNum(0);
    setSortModel(nextSort?.sort && isChannelMarkupV2SortField(nextSort.field) ? [nextSort] : []);
  }, []);

  const dataRows = channelMarkupList.map((row, index) => ({
    id: row.channelMarkupId ?? index,
    ...row,
  }));

  const handleEditChannelMarkup = useCallback(
    (row: ChannelMarkupV2Internal) => {
      push(`/marketing/channel-markup-v2/${row.channelMarkupId}/edit`, { channelMarkup: row });
    },
    [push],
  );
  const handleDeleteChannelMarkup = useCallback(
    async (row: ChannelMarkupV2Internal) => {
      await confirmAndDelete(row.channelMarkupId);
    },
    [confirmAndDelete],
  );
  const deletingChannelMarkupId = deleteChannelMarkupMutation.isPending
    ? deleteChannelMarkupMutation.variables
    : undefined;
  const columns = getColumns({
    tenant,
    canEditMarkup,
    deletingChannelMarkupId,
    handleEditChannelMarkup,
    handleDeleteChannelMarkup,
  });

  return (
    <Container>
      <Helmet>
        <title>Channel Markup v2</title>
      </Helmet>

      <PageSubheader title={`Channel Markup v2 [${channelMarkupList.length} of ${totalCount}]`} />

      {!canEditMarkup && (
        <Alert severity="warning" variant="outlined" sx={{ mt: 2 }}>
          You can view channel markup versions, but you do not have permission to create or update channel markup v2.
        </Alert>
      )}

      <Grid container spacing={2} justifyContent="flex-end" alignItems="end">
        <Grid item>
          <InputLabel>Status</InputLabel>
          <Select
            value={status}
            label="Status"
            onChange={(event) => handleStatusChange(getChannelMarkupStatus(event.target.value))}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
          </Select>
        </Grid>
        {canEditMarkup && (
          <Grid item>
            <Button variant="contained" color="primary" component={Link} to="/marketing/channel-markup-v2/create">
              Create
            </Button>
          </Grid>
        )}
      </Grid>

      {isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Channel Markup v2 fetch failed. Please try again.
        </Alert>
      )}

      <Box mt={2}>
        <DataGrid
          slots={{ pagination: GridPagination }}
          onPaginationModelChange={({ page, pageSize: newPageSize }) => onPageChange(page, newPageSize)}
          loading={isFetching}
          rows={dataRows}
          columns={columns}
          autoHeight
          rowCount={totalCount}
          paginationMode="server"
          pageSizeOptions={[10, 20, 50]}
          paginationModel={{ page: pageNum, pageSize }}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          getRowHeight={() => 'auto'}
        />
      </Box>
    </Container>
  );
}
